import { useEffect, useState } from "react";
import { AlertCircle, X } from "lucide-react";
import { useUsage } from "@/lib/usage";
import { openUpgrade } from "@/lib/billing";

const DISMISSED_KEY_PREFIX = "snaplist.usageBannerDismissed."; // suffix = month

/** Non-blocking banner at the top of the page when the user crosses 80% of
 *  the monthly AI quota. Dismissible for the current month; auto-returns the
 *  next calendar month on the first page load after rollover. */
export default function UsageNearLimitBanner() {
  const { nearLimit, overLimit, used, limit, month, plan } = useUsage();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return month ? window.localStorage.getItem(DISMISSED_KEY_PREFIX + month) === "1" : false;
    } catch { return false; }
  });

  // Re-sync dismissed flag whenever the month key changes (rollover).
  useEffect(() => {
    if (!month) return;
    try { setDismissed(window.localStorage.getItem(DISMISSED_KEY_PREFIX + month) === "1"); } catch { /* ignore */ }
  }, [month]);

  const dismiss = () => {
    try { if (month) window.localStorage.setItem(DISMISSED_KEY_PREFIX + month, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  if (dismissed || plan === "free" || (!nearLimit && !overLimit)) return null;

  const remaining = Math.max(0, limit - used);
  return (
    <div
      className={`mb-3 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${overLimit ? "border-rose-200 bg-rose-50 text-rose-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}
      role="status"
      data-testid="usage-near-limit-banner"
    >
      <AlertCircle className={`mt-0.5 h-4 w-4 shrink-0 ${overLimit ? "text-rose-600" : "text-amber-600"}`} />
      <div className="min-w-0 flex-1">
        {overLimit ? (
          <p>
            <span className="font-semibold">You've used all {limit} AI drafts for this month.</span>{" "}
            AI Generate is paused until the 1st —{" "}
            <button type="button" onClick={() => openUpgrade()} className="font-semibold underline" data-testid="usage-banner-go-pro">
              go Pro for unlimited drafts
            </button>.
          </p>
        ) : (
          <p>
            <span className="font-semibold">{remaining} AI draft{remaining === 1 ? "" : "s"} left this month.</span>{" "}
            Keep going — your counter resets on the 1st.
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 rounded-md p-1 opacity-60 hover:bg-white/60 hover:opacity-100"
        aria-label="Dismiss this notice"
        data-testid="usage-near-limit-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
